import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SEOHead } from '@/components/seo';
import { Header, Footer } from '@/components/public';
import { GuidesEnquiryModal } from '@/components/public/GuidesEnquiryModal';
import { ArrowRight, Rocket, Users, TrendingUp, Cpu, Wallet, ShieldCheck } from 'lucide-react';
import { matchCategory } from '@/data/solutions';

// ── Palette — aligned with the country pages (green StartEase theme) ────────
const BG = '#0b1310';
const BAND = '#070d0b';
const PANEL = '#111c17';
const GREEN = '#059669';
const GREEN_SOFT = '#34d399';
const BODY = '#9aa8b4';
const MUTED = '#64748b';
const LINE = 'rgba(255,255,255,0.10)';

// ── Six stable solution categories ──────────────────────────────────────────
const CATEGORIES = [
  ['Start & Expand', 'start-expand', Rocket, 'Company formation, market entry, banking & expansion'],
  ['People & Payroll', 'people-payroll', Users, 'EOR, payroll, contractors & HR anywhere'],
  ['Sales & Growth', 'sales-growth', TrendingUp, 'Lead generation, sales intelligence, CRM & outreach'],
  ['Technology & AI', 'technology-ai', Cpu, 'IT, AI, automation, integrations & cybersecurity'],
  ['Finance & Operations', 'finance-operations', Wallet, 'Payments, accounting, expenses & financial systems'],
  ['Compliance & Legal', 'compliance-legal', ShieldCheck, 'Tax, filings, legal documents & regulatory support'],
];

// ── Leading enterprise platforms (unified logo wall) ────────────────────────
// (brand list imported from '@/data/solutions')

// ── Discovery suggestion chips → matching category ──────────────────────────
const CHIPS = [
  ['Start a company', 'start-expand'],
  ['Hire internationally', 'people-payroll'],
  ['Run payroll', 'people-payroll'],
  ['Find customers', 'sales-growth'],
  ['Manage compliance', 'compliance-legal'],
  ['Automate a workflow', 'technology-ai'],
  ['Get IT support', 'technology-ai'],
];

// Hero discovery — 3 things we deliver directly, highlighted (ops first).
const WE_DO_CHIPS = [
  ['Start a company', '/usa'],
  ['Managed payroll', '/usa/payroll'],
  ['Managed sales tax', '/usa/sales-tax'],
  ['Managed bookkeeping', '/usa/bookkeeping'],
];

// ── Reusable atoms ────────────────────────────────────────────────────────
const Kicker = ({ children, className = '', style }) => (
  <span className={`inline-block font-sans text-[11px] font-semibold uppercase tracking-[0.22em] ${className}`} style={{ color: MUTED, ...style }}>
    {children}
  </span>
);

const Eyebrow = ({ n, label }) => (
  <div className="mb-5 flex items-center gap-3">
    <span className="font-sans text-[11px] tracking-[0.22em]" style={{ color: GREEN }}>{n}</span>
    <span className="inline-block h-px w-6" style={{ background: GREEN }} />
    <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.22em]" style={{ color: MUTED }}>{label}</span>
  </div>
);

const GreenButton = ({ to, href, children, testId }) => {
  const base = 'group inline-flex min-h-[48px] items-center gap-2 rounded-lg px-6 py-3.5 font-sans text-[14px] font-semibold text-white transition-colors hover:bg-[#047857] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#059669] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b1310]';
  const style = { background: GREEN };
  const inner = <>{children}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></>;
  return to
    ? <Link to={to} className={base} style={style} data-testid={testId}>{inner}</Link>
    : <a href={href} className={base} style={style} data-testid={testId}>{inner}</a>;
};

// ── Keyword → category matcher for the discovery search ─────────────────────
// ── Page ──────────────────────────────────────────────────────────────────
const Homepage = () => {
  const navigate = useNavigate();
  const [consultOpen, setConsultOpen] = useState(false);
  const [heroQuery, setHeroQuery] = useState('');
  const [stillQuery, setStillQuery] = useState('');

  const runSearch = (text) => {
    const q = (text || '').trim();
    const slug = matchCategory(q);
    navigate('/usa');
    window.scrollTo(0, 0);
  };

  return (
    <div className="min-h-screen" style={{ background: BG, color: '#e2e8f0' }} data-testid="homepage">
      <SEOHead
        title="StartEase Agent — Start, Run & Grow Your US Business"
        description="StartEase delivers US company formation, compliance, operations, payroll and sales tax in-house — everything your US business needs to start, stay compliant and grow, in one place."
        ogDescription="StartEase delivers US company formation, compliance, operations, payroll and sales tax in-house — everything your US business needs, in one place."
        ogType="website"
        canonical="https://starteaseai.com"
      >
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'StartEase Agent',
            url: 'https://starteaseai.com',
            logo: 'https://customer-assets.emergentagent.com/job_ease-backend/artifacts/4auzvo9b_StartEase%20Logo.png',
            description: 'StartEase delivers US company formation, compliance, operations, payroll and sales tax in-house — everything your US business needs to start, stay compliant and grow.',
            email: 'hi@starteaseai.com',
            telephone: '+1-205-624-5819',
            contactPoint: {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              email: 'hi@starteaseai.com',
              telephone: '+1-205-624-5819',
              availableLanguage: 'English',
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'StartEase Agent',
            url: 'https://starteaseai.com',
          })}
        </script>
      </SEOHead>

      <Header />

      {/* ─── 1. HERO ─────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden text-white" style={{ background: BAND }} data-testid="hero-section">
        <div className="relative mx-auto max-w-6xl px-6 py-20 md:py-24">
          <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-6">
              <h1 className="mt-5 font-display text-[30px] leading-[1.1] tracking-[-0.01em] sm:text-[38px] md:text-[42px] lg:text-[46px]" data-testid="hero-headline">
                Start, run and grow<br />
                <span style={{ color: GREEN_SOFT }}>your US business.</span>
              </h1>
              <p className="mt-6 max-w-xl font-sans text-[16.5px] leading-[1.7]" style={{ color: BODY }}>
                Company formation, compliance, operations, payroll and sales tax — delivered in-house by
                StartEase, end to end.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
                <GreenButton to="/usa" testId="hero-cta-solutions">Explore US Services</GreenButton>
                <button onClick={() => setConsultOpen(true)} className="group inline-flex items-center gap-1.5 font-sans text-[14px] font-semibold" style={{ color: GREEN_SOFT }} data-testid="hero-cta-meeting">
                  Book a Meeting <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </button>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-1 font-sans text-[13px]" style={{ color: BODY }} data-testid="hero-trust-line">
                <span>Start</span>
                <span style={{ color: MUTED }}>/</span>
                <span>Operate</span>
                <span style={{ color: MUTED }}>/</span>
                <span>Grow — <span className="font-semibold" style={{ color: GREEN_SOFT }}>in the US</span></span>
              </div>
            </div>

            {/* Right — discovery */}
            <div className="lg:col-span-6" data-testid="discovery-section">
              <div className="rounded-2xl border p-6 md:p-8" style={{ borderColor: LINE, background: 'rgba(255,255,255,0.03)' }}>
                <h2 className="font-display text-[24px] leading-[1.15] tracking-[-0.01em] md:text-[30px] text-white" data-testid="discovery-heading">
                  What do you need help with?
                </h2>
                <div className="mt-2" data-testid="discovery-chips">
                  <p className="mb-2.5 font-mono text-[11px] uppercase tracking-[0.14em]" style={{ color: GREEN_SOFT }}>We handle this</p>
                  <div className="flex flex-wrap gap-2.5">
                    {WE_DO_CHIPS.map(([label, to]) => (
                      <Link key={label} to={to} onClick={() => window.scrollTo(0, 0)}
                        className="rounded-full border px-4 py-2 font-sans text-[13px] font-semibold transition-colors"
                        style={{ borderColor: 'rgba(5,150,105,0.5)', color: GREEN_SOFT, background: 'rgba(5,150,105,0.12)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(5,150,105,0.22)'; e.currentTarget.style.color = '#fff'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(5,150,105,0.12)'; e.currentTarget.style.color = GREEN_SOFT; }}
                        data-testid={`discovery-wedo-${to.split('/').pop()}`}>
                        {label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 1a. WHAT WE DELIVER (3 in-house services) ───────────────────── */}
      <section className="relative border-t" style={{ background: BG, borderColor: LINE }} data-testid="deliver-section">
        <div className="mx-auto max-w-6xl px-6 py-16 md:py-20">
          <div className="max-w-2xl">
            <p className="font-mono text-[12px]" style={{ color: GREEN_SOFT }}>// what we deliver</p>
            <h2 className="mt-3 font-display text-[30px] leading-[1.1] tracking-[-0.01em] md:text-[42px]" style={{ color: '#fff' }} data-testid="deliver-heading">
              The US services we run ourselves.
            </h2>
            <p className="mt-4 font-sans text-[16px] leading-[1.7]" style={{ color: BODY }}>
              Everything your US business needs to start, stay compliant and operate — delivered directly by StartEase, end to end.
            </p>
          </div>
          <div className="mt-12 grid gap-4 md:grid-cols-2">
            {[
              ['01', 'Company Formation', 'Incorporate your US LLC or C-Corp, get your EIN and registered agent — one clean record from day one.', '/usa', 'from $199'],
              ['02', 'Managed Payroll', 'Full-service US payroll — we file and pay every federal, state and local obligation, on flat monthly fees.', '/usa/payroll', 'from $150 / mo'],
              ['03', 'Managed Sales Tax', 'A real team registers, files and remits your US sales tax across all 50 states — priced per filing.', '/usa/sales-tax', 'from $75 / filing'],
              ['04', 'Managed Bookkeeping', 'Certified QuickBooks ProAdvisors keep your books accurate and tax-ready, every month.', '/usa/bookkeeping', 'from $150 / mo'],
            ].map(([n, title, desc, to, price]) => (
              <Link key={title} to={to} onClick={() => window.scrollTo(0, 0)}
                className="group flex flex-col rounded-2xl border p-7 transition-colors hover:border-[#059669]"
                style={{ borderColor: LINE, background: 'rgba(255,255,255,0.02)' }}
                data-testid={`deliver-card-${n}`}>
                <div className="flex items-start justify-between gap-3">
                  <span className="font-mono text-[12px]" style={{ color: MUTED }}>{n}</span>
                  {price && (
                    <span className="rounded-full border px-3 py-1 font-mono text-[11px] tracking-[0.04em]"
                      style={{ borderColor: 'rgba(52,211,153,0.3)', color: GREEN_SOFT, background: 'rgba(16,185,129,0.08)' }}
                      data-testid={`deliver-price-${n}`}>{price}</span>
                  )}
                </div>
                <h3 className="mt-3 font-display text-[21px] leading-tight text-white">{title}</h3>
                <p className="mt-3 flex-1 font-sans text-[14.5px] leading-[1.7]" style={{ color: BODY }}>{desc}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 font-sans text-[13.5px] font-semibold transition-colors group-hover:text-[#34d399]" style={{ color: GREEN_SOFT }}>
                  Learn more <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 1b. PRICING — Payroll · Sales Tax · Accounting ──────────────── */}
      <section className="relative border-t" style={{ background: BAND, borderColor: LINE }} data-testid="pricing-section">
        <div className="mx-auto max-w-6xl px-6 py-16 md:py-20">
          <div className="max-w-2xl">
            <p className="font-mono text-[12px]" style={{ color: GREEN_SOFT }}>// transparent pricing</p>
            <h2 className="mt-3 font-display text-[30px] leading-[1.1] tracking-[-0.01em] md:text-[42px]" style={{ color: '#fff' }} data-testid="pricing-heading">
              Simple, flat pricing.
            </h2>
            <p className="mt-4 font-sans text-[16px] leading-[1.7]" style={{ color: BODY }}>
              Know what you’ll pay before you commit — our management fee, kept separate from government and software costs.
            </p>
          </div>
          <div className="mt-12 grid gap-4 md:grid-cols-3" data-testid="pricing-grid">
            {[
              ['Managed Payroll', '$150', '/ month', 'Single-state payroll for up to 5 employees — we file and pay every federal, state and local obligation.', '/usa/payroll', 'payroll'],
              ['Managed Sales Tax', '$75', '/ filing', 'Per-return filing across all 50 states — we register, file and remit on time, every time.', '/usa/sales-tax', 'sales-tax'],
              ['Managed Accounting', '$150', '/ month', 'Monthly bookkeeping by Certified QuickBooks ProAdvisors — categorized, reconciled and tax-ready.', '/usa/bookkeeping', 'accounting'],
            ].map(([title, price, unit, desc, to, key]) => (
              <div key={title} className="flex flex-col rounded-2xl border p-7 transition-colors hover:border-[#059669]"
                style={{ borderColor: LINE, background: PANEL }}
                data-testid={`pricing-card-${key}`}>
                <h3 className="font-display text-[21px] leading-tight text-white">{title}</h3>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-display text-[40px] leading-none text-white" data-testid={`pricing-amount-${key}`}>{price}</span>
                  <span className="font-mono text-[13px]" style={{ color: GREEN_SOFT }}>{unit}</span>
                </div>
                <p className="mt-5 flex-1 font-sans text-[14px] leading-[1.7]" style={{ color: BODY }}>{desc}</p>
                <Link to={to} onClick={() => window.scrollTo(0, 0)}
                  className="group mt-7 inline-flex min-h-[46px] items-center justify-center gap-2 rounded-lg px-5 py-3 font-sans text-[14px] font-semibold text-white transition-colors hover:bg-[#047857]"
                  style={{ background: GREEN }}
                  data-testid={`pricing-cta-${key}`}>
                  View {title.replace('Managed ', '').toLowerCase()} pricing
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>
            ))}
          </div>
          <p className="mt-6 font-sans text-[13px]" style={{ color: MUTED }} data-testid="pricing-note">
            Government fees and software subscriptions are billed to you directly, at cost — never marked up.
          </p>
        </div>
      </section>

      {/* ─── 6c. TRUSTED BY — illustrative wordmarks (fictional names) ───── */}
      <section className="relative" style={{ background: BG }} data-testid="trusted-section">
        <div className="mx-auto max-w-6xl px-6 py-14 md:py-16">
          <Kicker>Trusted by</Kicker>
          <h2 className="mt-3 max-w-2xl font-display text-[24px] leading-[1.2] tracking-[-0.01em] md:text-[30px]" style={{ color: '#fff' }} data-testid="trusted-heading">
            Businesses building and growing with StartEase.
          </h2>
          <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="trusted-logos">
            {['Nuvora', 'Kestrix', 'Meridio', 'Palvora', 'Brightkelp', 'Orenda',
              'Tavil', 'Vandry', 'Solventa', 'Quillbridge', 'Northaven', 'Zephira'].map((name) => (
              <div key={name} data-testid={`trusted-logo-${name.toLowerCase()}`}
                className="group flex items-center gap-3 rounded-xl border px-4 py-3.5 transition-all duration-300"
                style={{ borderColor: 'rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.015)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(5,150,105,0.55)';
                  e.currentTarget.style.background = 'rgba(5,150,105,0.06)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                  e.currentTarget.style.background = 'rgba(255,255,255,0.015)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}>
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg font-display text-[16px] font-bold"
                  style={{ background: 'rgba(5,150,105,0.14)', color: GREEN_SOFT }}>
                  {name[0]}
                </span>
                <span className="font-display text-[16px] md:text-[17px] font-semibold tracking-tight truncate"
                  style={{ color: 'rgba(255,255,255,0.72)' }}>
                  {name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 7. CLOSING CTA ──────────────────────────────────────────────── */}
      <section className="relative text-white" style={{ background: BG }} data-testid="closing-section">
        <div className="mx-auto max-w-4xl px-6 py-20 text-center md:py-24">
          <h2 className="font-display leading-[1.05] tracking-[-0.015em]" style={{ fontSize: 'clamp(36px, 5.5vw, 58px)' }}>
            Not sure what you need?
          </h2>
          <p className="mx-auto mt-6 max-w-2xl font-sans text-[16px] leading-[1.7]" style={{ color: BODY }}>
            Tell us what you're trying to accomplish.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
            <button
              type="button"
              onClick={() => setConsultOpen(true)}
              className="inline-flex min-h-[48px] items-center gap-2 rounded-full px-7 font-sans text-[15px] font-semibold text-white transition-colors"
              style={{ background: GREEN }}
              onMouseEnter={(e) => (e.currentTarget.style.background = GREEN_SOFT)}
              onMouseLeave={(e) => (e.currentTarget.style.background = GREEN)}
              data-testid="closing-cta-contact"
            >
              Book a Meeting
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          <dl className="mx-auto mt-14 grid max-w-3xl grid-cols-1 border-t border-b sm:grid-cols-3" style={{ borderColor: LINE }}>
            {[['One place', 'for everything US'], ['Delivered by us', 'in-house, end to end'], ['Implemented', 'and supported for you']].map(([title, sub], i) => (
              <div key={title} className="px-6 py-6" style={i > 0 ? { borderLeft: `1px solid ${LINE}` } : undefined}>
                <dt className="font-display text-[17px] leading-tight text-white">{title}</dt>
                <dd className="mt-2 font-sans text-[13px]" style={{ color: MUTED }}>{sub}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <Footer />
      <GuidesEnquiryModal open={consultOpen} onOpenChange={setConsultOpen} />
    </div>
  );
};

export default Homepage;
